/**
 * cartToReceipt — adapter that turns the LIVE billing cart into the same
 * framework-agnostic ReceiptData the printer consumes, so a preview rendered
 * from this is byte-identical to what will actually print.
 *
 * Sibling of invoiceToReceipt (which reads a persisted invoice); this one reads
 * the in-memory cart so the preview updates as items are added. Kept pure — no
 * DB, no store imports — so it is trivially testable and stays decoupled from
 * the state layer (callers pass plain values).
 */

import type { ComputedLineItem, InvoiceTotals } from '@/billing/billingEngine';
import type { ReceiptData, ReceiptLineItem, ReceiptTaxLine } from '../contracts/ReceiptContracts';

/** Minimal store fields the receipt header needs (decoupled from the DB model). */
export interface CartReceiptStore {
  name: string;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  pincode?: string | null;
  gstin?: string | null;
  phone?: string | null;
}

interface PreviewPayment {
  method: string;
  amount: number; // rupees
}

interface PreviewCustomer {
  name?: string | null;
  phone?: string | null;
}

export interface CartToReceiptInput {
  items: ComputedLineItem[];
  totals: InvoiceTotals;
  store: CartReceiptStore;
  customer?: PreviewCustomer | null;
  payments?: PreviewPayment[];
  /** Defaults to a "— PREVIEW —" marker since no invoice number exists yet. */
  invoiceNumber?: string;
  /** Injectable for stable tests. Defaults to now. */
  now?: Date;
}

const paisaToInr = (p: number): number => p / 100;

const formatDate = (d: Date): string =>
  d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

export function cartToReceipt(input: CartToReceiptInput): ReceiptData {
  const { items, totals, store, customer, payments = [] } = input;

  const addressLine = [store.address, store.city, store.state, store.pincode]
    .map((s) => (s ?? '').trim())
    .filter(Boolean)
    .join(', ');

  // Mirror invoiceToReceipt's per-line discount %: derive from the absolute
  // discount vs the gross line value so it reads correctly however it was
  // entered (flat ₹ vs %).
  const receiptItems: ReceiptLineItem[] = items.map((li) => {
    const pct =
      li.grossAmountPaisa > 0 && li.discountAmountPaisa > 0
        ? Math.round((li.discountAmountPaisa / li.grossAmountPaisa) * 1000) / 10
        : 0;
    return {
      name: li.productName,
      qty: li.quantity,
      // Rule 46(f): quantity and unit. Without this the on-screen receipt
      // preview would disagree with what the printer actually produces.
      unit: li.unit || undefined,
      rate: li.unitPrice,
      amount: paisaToInr(li.totalPaisa),
      hsn: li.hsnCode || undefined,
      discountPct: pct > 0 ? pct : undefined,
    };
  });

  const taxes: ReceiptTaxLine[] = [];
  if (totals.cgst > 0) taxes.push({ label: 'CGST', amount: totals.cgst });
  if (totals.sgst > 0) taxes.push({ label: 'SGST', amount: totals.sgst });
  if (totals.igst > 0) taxes.push({ label: 'IGST', amount: totals.igst });
  if (totals.cess > 0) taxes.push({ label: 'CESS', amount: totals.cess });

  const paid = payments.reduce((sum, p) => sum + p.amount, 0);
  const change = paid > totals.totalAmount ? paid - totals.totalAmount : undefined;
  const methods = payments.map((p) => p.method).join('/');

  return {
    storeName: store.name,
    addressLine: addressLine || undefined,
    gstin: store.gstin || undefined,
    phone: store.phone || undefined,
    invoiceNumber: input.invoiceNumber ?? '— PREVIEW —',
    invoiceDate: formatDate(input.now ?? new Date()),
    customerName: customer?.name?.trim() || customer?.phone || undefined,
    items: receiptItems,
    subtotal: totals.subtotal,
    discount: totals.globalDiscount > 0 ? totals.globalDiscount : undefined,
    taxes,
    total: totals.totalAmount,
    payment: payments.length > 0 ? { method: methods || 'CASH', paid, change } : undefined,
    // Footer message + powered-by come from the store's ReceiptConfig, not here.
  };
}
